import React, {useCallback, useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import Workspace from "../components/Workspace";
import {getDashboardStats, getMyListings} from "../api/listings";
import {getMyProjects} from "../api/projects";
import {getMyLeads} from "../api/leads";
import OverviewTab from "../dashboard/OverviewTab";
import ListingsTab from "../dashboard/ListingsTab";
import ProjectsTab from "../dashboard/ProjectsTab";
import LeadsTab from "../dashboard/LeadsTab";
import ProfileTab from "../dashboard/ProfileTab";
import SiteStaffTab from "../dashboard/SiteStaffTab";

const TABS = [
    {id: "overview", label: "Overview"},
    {id: "listings", label: "Listings"},
    {id: "projects", label: "Projects"},
    {id: "leads", label: "Leads"},
    {id: "staff", label: "Site staff", builderOnly: true},
    {id: "profile", label: "Profile"},
];

const DashboardBody = ({broker, setBroker}) => {
    const [params, setParams] = useSearchParams();
    const tabs = TABS.filter((t) => !t.builderOnly || broker.accountType === "BUILDER");
    const tab = tabs.some((t) => t.id === params.get("tab")) ? params.get("tab") : "overview";
    const [data, setData] = useState({stats: null, listings: [], projects: [], leads: []});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const refresh = useCallback(async () => {
        setError("");
        try {
            const [stats, listings, projects, leads] = await Promise.all([
                getDashboardStats(), getMyListings(), getMyProjects(), getMyLeads(),
            ]);
            setData({stats, listings, projects, leads});
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const go = (id) => setParams(id === "overview" ? {} : {tab: id});
    const standalone = data.listings.filter((l) => !l.projectId);
    const newLeads = data.leads.filter((l) => l.status === "NEW").length;
    const counts = {listings: standalone.length, projects: data.projects.length, leads: newLeads || null};
    const approved = broker.status === "APPROVED";

    return (
        <>
            <div className="brk-page-head">
                <div>
                    <h1 className="brk-page-title">Welcome, {broker.fullName.split(" ")[0]}</h1>
                    <p className="brk-page-subtitle">{broker.companyName}</p>
                </div>
            </div>
            <nav className="brk-tabs" role="tablist" aria-label="Dashboard sections">
                {tabs.map((t) => (
                    <button key={t.id} role="tab" aria-selected={tab === t.id}
                            className={`brk-tab${tab === t.id ? " brk-tab--on" : ""}`} onClick={() => go(t.id)}>
                        {t.label}
                        {counts[t.id] ? (
                            <span className={`brk-tab__count${t.id === "leads" ? "" : " brk-tab__count--muted"}`}>{counts[t.id]}</span>
                        ) : null}
                    </button>
                ))}
            </nav>
            {error && <div className="brk-notice brk-notice--error" role="alert">{error}</div>}
            {loading ? <div className="brk-loading">Loading…</div> : (
                <>
                    {tab === "overview" && <OverviewTab broker={broker} {...data} onGo={go} approved={approved}/>}
                    {tab === "listings" && <ListingsTab listings={standalone} approved={approved} onChanged={refresh}/>}
                    {tab === "projects" && <ProjectsTab projects={data.projects} broker={broker} approved={approved} onChanged={refresh}/>}
                    {tab === "leads" && <LeadsTab leads={data.leads} onChanged={refresh}/>}
                    {tab === "staff" && <SiteStaffTab projects={data.projects} approved={approved}/>}
                    {tab === "profile" && <ProfileTab broker={broker} onSaved={setBroker}/>}
                </>
            )}
        </>
    );
};

const DashboardPage = () => (
    <Workspace>{({broker, setBroker}) => <DashboardBody broker={broker} setBroker={setBroker}/>}</Workspace>
);

export default DashboardPage;
